import React from 'react';
import { Portal, Box, Typography, Button } from '@mui/material';

const WEEKDAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function formatOrdinalDateEn(d) {
  if (!d) return '';
  const day = d.getDate();
  const k = day % 100;
  const suf =
    k > 10 && k < 14
      ? 'th'
      : day % 10 === 1
        ? 'st'
        : day % 10 === 2
          ? 'nd'
          : day % 10 === 3
            ? 'rd'
            : 'th';
  const mon = d.toLocaleDateString('en-US', { month: 'short' });
  return `${day}${suf} ${mon}, ${d.getFullYear()}`;
}

/**
 * Full-screen mobile "Search Date" drawer shared by FlightForm and FlightSelectHeader.
 * Fixed weekday header sits between the sheet header and the scroll area (never scrolls).
 * Calendar day-name rows inside the DatePicker are hidden via CSS so they aren't duplicated.
 */
function MobileSearchDateSheet({
  open,
  onClose,
  onSave,
  tripType,
  dateDraftStart,
  dateDraftEnd,
  children,
  sheetClassName = '',
  backdropClassName = '',
  /** 'hotel' = check-in/out labels + Save requires both dates (homepage hotel). Flight behavior unchanged when omitted. */
  variant = 'flight',
  /** Hotel only: calendar selection mode driven by taps on footer segments (parity with legacy hotel fullscreen). */
  hotelDateSegmentFocused = null,
  onHotelDateSegmentFocus,
  /** Round-trip flight: which leg the calendar is picking (departure = outbound fares). */
  dateSegmentFocused = 'departure',
  onDateSegmentFocus,
  onClearDeparture,
  onClearReturn,
  onClearCheckin,
  onClearCheckout,
}) {
  if (!open) return null;

  const isRoundTrip = tripType === 'roundtrip';
  const isHotel = variant === 'hotel';
  const hotelFooterInteractive =
    isHotel &&
    typeof onHotelDateSegmentFocus === 'function';
  const flightFooterInteractive =
    !isHotel &&
    isRoundTrip &&
    typeof onDateSegmentFocus === 'function';

  const saveDisabled = isHotel || isRoundTrip
    ? !dateDraftStart || !dateDraftEnd
    : !dateDraftStart;

  const summaryLabelSx = {
    fontFamily: 'Host Grotesk',
    fontSize: '12px',
    fontWeight: 500,
    color: '#535462',
    lineHeight: '16px',
    letterSpacing: '0.25px',
    textAlign: 'left',
  };

  const summaryValueSx = {
    fontFamily: 'Host Grotesk',
    fontSize: '16px',
    fontWeight: 600,
    color: '#1A1827',
    lineHeight: '22px',
    mt: '2px',
    textAlign: 'left',
  };

  const renderSummaryClear = (onClear, ariaLabel) => {
    if (typeof onClear !== 'function') return null;
    return (
      <button
        type="button"
        className="mobile-date-summary-clear"
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
      >
        <svg width="6" height="6" viewBox="0 0 6 6" fill="none" aria-hidden>
          <path d="M1 1L5 5M5 1L1 5" stroke="#FBFBFB" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </button>
    );
  };

  const summaryChipClass = (...parts) => parts.filter(Boolean).join(' ');

  const summaryChipInteractiveClass = 'mobile-date-fullscreen-summary-box--interactive';

  return (
    <Portal>
      <Box
        className={['mobile-date-fullscreen-backdrop', backdropClassName].filter(Boolean).join(' ')}
        onClick={onClose}
        aria-hidden
      />
      <Box
        data-mobile-date-sheet
        data-date-picker
        className={['mobile-date-fullscreen-sheet', sheetClassName].filter(Boolean).join(' ')}
      >
        {/* Drag handle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: '10px', pb: '2px', flexShrink: 0 }}>
          <Box sx={{ width: '36px', height: '4px', borderRadius: '2px', background: '#D0D0D8' }} />
        </Box>

        {/* Header — "Search Date" + close */}
        <Box className="mobile-date-fullscreen-header">
          <Typography sx={{ fontFamily: 'Host Grotesk', fontSize: '18px', fontWeight: 700, color: '#20202A', lineHeight: '26px' }}>
            Search Date
          </Typography>
          <Box
            onClick={onClose}
            sx={{ p: '6px', cursor: 'pointer', WebkitTapHighlightColor: 'transparent', ml: '8px' }}
            role="button"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#20202A" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </Box>
        </Box>

        {/* Fixed weekday row — single row outside scroll, matches required design */}
        <Box className="mobile-date-sheet-weekday-row">
          {WEEKDAY_LABELS.map((label) => (
            <span key={label} className="mobile-date-sheet-weekday-cell">{label}</span>
          ))}
        </Box>

        {/* Scrollable calendar area */}
        <Box className="mobile-date-fullscreen-scroll">{children}</Box>

        {/* Footer — summary boxes + Save */}
        <Box className="mobile-date-fullscreen-footer">
          {isHotel ? (
            <Box className="mobile-date-fullscreen-summary-row">
              <Box className="mobile-date-summary-box-wrap">
              <Box
                component={hotelFooterInteractive ? 'button' : 'div'}
                type={hotelFooterInteractive ? 'button' : undefined}
                className={summaryChipClass(
                  'mobile-date-fullscreen-summary-box',
                  dateDraftStart ? 'active' : '',
                  hotelFooterInteractive ? summaryChipInteractiveClass : '',
                  hotelFooterInteractive && hotelDateSegmentFocused === 'checkin' ? 'hotel-date-segment-focused' : ''
                )}
                data-segment-focused={hotelFooterInteractive && hotelDateSegmentFocused === 'checkin' ? 'true' : undefined}
                tabIndex={hotelFooterInteractive ? 0 : undefined}
                onClick={
                  hotelFooterInteractive ? () => onHotelDateSegmentFocus('checkin') : undefined
                }
                onKeyDown={
                  hotelFooterInteractive
                    ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onHotelDateSegmentFocus('checkin');
                      }
                    }
                    : undefined
                }
              >
                <Typography sx={summaryLabelSx}>
                  Check-in Date
                </Typography>
                <Typography sx={summaryValueSx}>
                  {dateDraftStart ? formatOrdinalDateEn(dateDraftStart) : '—'}
                </Typography>
              </Box>
              {dateDraftStart && renderSummaryClear(onClearCheckin, 'Clear check-in date')}
              </Box>
              <Box className="mobile-date-summary-box-wrap">
              <Box
                component={hotelFooterInteractive ? 'button' : 'div'}
                type={hotelFooterInteractive ? 'button' : undefined}
                className={summaryChipClass(
                  'mobile-date-fullscreen-summary-box',
                  dateDraftEnd ? 'active' : '',
                  hotelFooterInteractive ? summaryChipInteractiveClass : '',
                  hotelFooterInteractive && hotelDateSegmentFocused === 'checkout' ? 'hotel-date-segment-focused' : ''
                )}
                data-segment-focused={hotelFooterInteractive && hotelDateSegmentFocused === 'checkout' ? 'true' : undefined}
                tabIndex={hotelFooterInteractive ? 0 : undefined}
                onClick={
                  hotelFooterInteractive ? () => onHotelDateSegmentFocus('checkout') : undefined
                }
                onKeyDown={
                  hotelFooterInteractive
                    ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onHotelDateSegmentFocus('checkout');
                      }
                    }
                    : undefined
                }
              >
                <Typography sx={summaryLabelSx}>
                  Check-out Date
                </Typography>
                <Typography
                  sx={{
                    ...summaryValueSx,
                    color: dateDraftEnd ? '#1A1827' : '#AEAEB8',
                    fontWeight: dateDraftEnd ? 600 : 500,
                  }}
                >
                  {dateDraftEnd ? formatOrdinalDateEn(dateDraftEnd) : 'Select check-out'}
                </Typography>
              </Box>
              {dateDraftEnd && renderSummaryClear(onClearCheckout, 'Clear check-out date')}
              </Box>
            </Box>
          ) : isRoundTrip ? (
            <Box className="mobile-date-fullscreen-summary-row">
              <Box className="mobile-date-summary-box-wrap">
              <Box
                component={flightFooterInteractive ? 'button' : 'div'}
                type={flightFooterInteractive ? 'button' : undefined}
                className={summaryChipClass(
                  'mobile-date-fullscreen-summary-box',
                  dateDraftStart ? 'active' : '',
                  flightFooterInteractive ? summaryChipInteractiveClass : '',
                  flightFooterInteractive && dateSegmentFocused === 'departure' ? 'flight-date-segment-focused' : ''
                )}
                data-segment-focused={flightFooterInteractive && dateSegmentFocused === 'departure' ? 'true' : undefined}
                tabIndex={flightFooterInteractive ? 0 : undefined}
                onClick={flightFooterInteractive ? () => onDateSegmentFocus('departure') : undefined}
                onKeyDown={
                  flightFooterInteractive
                    ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onDateSegmentFocus('departure');
                      }
                    }
                    : undefined
                }
              >
                <Typography sx={summaryLabelSx}>
                  Departure Date
                </Typography>
                <Typography sx={summaryValueSx}>
                  {dateDraftStart ? formatOrdinalDateEn(dateDraftStart) : '—'}
                </Typography>
              </Box>
              {dateDraftStart && renderSummaryClear(onClearDeparture, 'Clear departure date')}
              </Box>
              <Box className="mobile-date-summary-box-wrap">
              <Box
                component={flightFooterInteractive ? 'button' : 'div'}
                type={flightFooterInteractive ? 'button' : undefined}
                className={summaryChipClass(
                  'mobile-date-fullscreen-summary-box',
                  dateDraftEnd ? 'active' : 'mobile-date-fullscreen-summary-box--return-empty',
                  flightFooterInteractive ? summaryChipInteractiveClass : '',
                  flightFooterInteractive && dateSegmentFocused === 'return' ? 'flight-date-segment-focused' : ''
                )}
                data-segment-focused={flightFooterInteractive && dateSegmentFocused === 'return' ? 'true' : undefined}
                tabIndex={flightFooterInteractive ? 0 : undefined}
                onClick={flightFooterInteractive ? () => onDateSegmentFocus('return') : undefined}
                onKeyDown={
                  flightFooterInteractive
                    ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onDateSegmentFocus('return');
                      }
                    }
                    : undefined
                }
              >
                {dateDraftEnd ? (
                  <>
                    <Typography sx={summaryLabelSx}>
                      Return Date
                    </Typography>
                    <Typography sx={summaryValueSx}>
                      {formatOrdinalDateEn(dateDraftEnd)}
                    </Typography>
                  </>
                ) : (
                  <Typography component="span" className="mobile-date-summary-return-placeholder">
                    Return Date
                  </Typography>
                )}
              </Box>
              {dateDraftEnd && renderSummaryClear(onClearReturn, 'Clear return date')}
              </Box>
            </Box>
          ) : (
            <Box className="mobile-date-fullscreen-summary-row">
              <Box className="mobile-date-summary-box-wrap mobile-date-summary-box-wrap--full">
              <Box className={`mobile-date-fullscreen-summary-box single flight-date-segment-focused${dateDraftStart ? ' active' : ''}`} data-segment-focused="true">
                <Typography sx={summaryLabelSx}>
                  Departure Date
                </Typography>
                <Typography sx={summaryValueSx}>
                  {dateDraftStart ? formatOrdinalDateEn(dateDraftStart) : '—'}
                </Typography>
              </Box>
              {dateDraftStart && renderSummaryClear(onClearDeparture, 'Clear departure date')}
              </Box>
            </Box>
          )}
          <Button
            fullWidth
            variant="contained"
            disabled={saveDisabled}
            onClick={onSave}
            className="mobile-date-fullscreen-save"
          >
            Save
          </Button>
        </Box>
      </Box>
    </Portal>
  );
}

export default React.memo(MobileSearchDateSheet);
